import { useState } from "react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { toast } from "sonner";
import { Upload, Download, Users } from "lucide-react";

const badge = (a) => ({ create: "bg-green-100 text-green-800", update: "bg-blue-100 text-blue-800", skip: "bg-red-100 text-red-700", created: "bg-green-100 text-green-800", updated: "bg-blue-100 text-blue-800", skipped: "bg-red-100 text-red-700" }[a] || "bg-slate-100 text-slate-600");

export default function AdminImport() {
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [mode, setMode] = useState("shared");
  const [sharedPw, setSharedPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setResults(null);
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/import/preview", fd);
      setPreview(data);
    } catch (err) { toast.error(errMsg(err)); setPreview(null); }
    setBusy(false);
    e.target.value = "";
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/import/commit", {
        rows: preview.rows, password_mode: mode, shared_password: mode === "shared" ? sharedPw : null,
      });
      setResults(data);
      setPreview(null);
      toast.success(`Import complete — ${data.summary.created} created, ${data.summary.updated} updated, ${data.summary.skipped} skipped`);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const downloadResults = () => {
    const header = "row,name,login,status,temp_password,reason\n";
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = results.results.map((r) => [r.row, r.name, r.login, r.status, r.temp_password, r.reason].map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([header + body], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `site-wars-user-import-results-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl">
      <h3 className="font-display font-semibold text-[#1a1f36] flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-[#c8102e]" />Import users in bulk</h3>
      <p className="text-sm text-slate-600 mb-4">Upload a CSV or XLSX with columns <code className="bg-slate-100 px-1 rounded">name</code> (required), <code className="bg-slate-100 px-1 rounded">property</code> (required for property users), <code className="bg-slate-100 px-1 rounded">email</code> (optional — a username is generated if blank), <code className="bg-slate-100 px-1 rounded">role</code> (office / maintenance / admin / judge). Re-uploading the same file updates instead of duplicating. Nothing is emailed — hand out credentials via the results file.</p>

      <label className="inline-flex items-center gap-2 border-2 border-dashed border-slate-300 rounded-md px-5 py-3 cursor-pointer hover:border-[#c8102e] transition-colors text-sm text-slate-600 mb-5">
        <Upload className="w-4 h-4" /> {fileName || "Choose CSV or XLSX file…"}
        <input type="file" accept=".csv,.xlsx" className="hidden" onChange={upload} data-testid="import-file-input" />
      </label>
      {busy && <p className="text-sm text-slate-500 mb-4">Working…</p>}

      {preview && (
        <div className="fade-up">
          <div className="border border-slate-200 rounded-md p-4 mb-4 bg-[#f8f9fc]" data-testid="import-summary">
            <div className="font-semibold text-[#1a1f36] mb-1">
              Create {preview.summary.create} user{preview.summary.create === 1 ? "" : "s"} across {preview.summary.properties} propert{preview.summary.properties === 1 ? "y" : "ies"}?
            </div>
            <div className="text-sm text-slate-600">{preview.summary.update} will be updated · {preview.summary.skip} skipped ({preview.rows.filter((r) => r.action === "skip").length > 0 ? "see reasons below" : "none"})</div>
          </div>

          <div className="border border-slate-200 rounded-md p-4 mb-4">
            <Label className="font-semibold text-sm">Temporary password for new users</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="mt-2 space-y-2">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="shared" id="pm-shared" data-testid="import-mode-shared" />
                <Label htmlFor="pm-shared" className="text-sm font-normal">One shared temp password for everyone (forced reset on first sign-in)</Label>
              </div>
              {mode === "shared" && <Input type="text" placeholder="Shared temp password (min 8 chars)" value={sharedPw} onChange={(e) => setSharedPw(e.target.value)} className="max-w-xs ml-6" data-testid="import-shared-password" />}
              <div className="flex items-center gap-2">
                <RadioGroupItem value="per_user" id="pm-per" data-testid="import-mode-per-user" />
                <Label htmlFor="pm-per" className="text-sm font-normal">Unique temp password per user — returned in the results file (recommended for large groups)</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="border border-slate-200 rounded-md overflow-auto max-h-96 mb-4">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#f8f9fc] text-xs uppercase tracking-wide text-slate-500 sticky top-0">
                <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Login</th><th className="px-3 py-2">Property (matched)</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Action</th></tr>
              </thead>
              <tbody data-testid="import-preview-table">
                {preview.rows.map((r) => (
                  <tr key={r.row} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 text-slate-400">{r.row}</td>
                    <td className="px-3 py-1.5">{r.name || <span className="text-red-500 text-xs">missing</span>}</td>
                    <td className="px-3 py-1.5 text-xs">{r.email || (r.username && <span>{r.username} <span className="text-slate-400">(generated)</span></span>) || "—"}</td>
                    <td className="px-3 py-1.5 text-xs">
                      {r.account_role !== "property_user" ? <span className="text-slate-400">n/a ({r.account_role})</span>
                        : r.matched_property_name
                          ? <span>{r.matched_property_name}{r.property_input.toLowerCase() !== r.matched_property_name.toLowerCase() && <span className="text-[#c8102e]"> ← "{r.property_input}"</span>}</span>
                          : <span className="text-red-500">"{r.property_input}" not matched</span>}
                    </td>
                    <td className="px-3 py-1.5 text-xs">{r.account_role === "property_user" ? r.role : r.account_role}</td>
                    <td className="px-3 py-1.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badge(r.action)}`}>{r.action}{r.reason ? ` — ${r.reason}` : ""}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2">
            <Button onClick={commit} disabled={busy || (preview.summary.create + preview.summary.update === 0) || (mode === "shared" && sharedPw.length < 8)} className="sw-red hover:bg-red-700 text-white" data-testid="import-commit-btn">
              {busy ? "Importing…" : `Confirm — create ${preview.summary.create}, update ${preview.summary.update}`}
            </Button>
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="import-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}

      {results && (
        <div className="fade-up">
          <div className="border border-green-200 bg-green-50 rounded-md p-4 mb-3 flex items-center justify-between" data-testid="import-results-summary">
            <div className="text-sm text-green-900 font-medium">{results.summary.created} created · {results.summary.updated} updated · {results.summary.skipped} skipped</div>
            <Button size="sm" onClick={downloadResults} className="bg-[#1a1f36] hover:bg-[#2a3152] text-white" data-testid="import-download-results-btn">
              <Download className="w-4 h-4 mr-1" /> Download results CSV
            </Button>
          </div>
          <div className="border border-slate-200 rounded-md overflow-auto max-h-96">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#f8f9fc] text-xs uppercase tracking-wide text-slate-500 sticky top-0">
                <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Login</th><th className="px-3 py-2">Temp password</th><th className="px-3 py-2">Status</th></tr>
              </thead>
              <tbody data-testid="import-results-table">
                {results.results.map((r) => (
                  <tr key={`${r.row}-${r.login}`} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 text-slate-400">{r.row}</td>
                    <td className="px-3 py-1.5">{r.name}</td>
                    <td className="px-3 py-1.5 text-xs font-mono">{r.login}</td>
                    <td className="px-3 py-1.5 text-xs font-mono">{r.temp_password}</td>
                    <td className="px-3 py-1.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badge(r.status)}`}>{r.status}{r.reason ? ` — ${r.reason}` : ""}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
